'use client';

import React, { use } from 'react';
import LaporanHeader from '@/components/yayasan/keuangan/LaporanHeader';
import { SchoolUnit, validUnits, getUnitFinancialStats } from '@/lib/data';
import { notFound } from 'next/navigation';
import { BarChart3, TrendingUp, ArrowDownRight, ArrowUpRight, Plus, Minus, Calculator } from 'lucide-react';

export default function LaporanModalPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit: initialUnit } = use(params);
    const [activeUnit, setActiveUnit] = React.useState<SchoolUnit>(initialUnit as SchoolUnit);

    if (!validUnits.includes(initialUnit as SchoolUnit)) {
        notFound();
    }

    const stats = getUnitFinancialStats(activeUnit);

    const MODAL_CHANGES = [
        { label: 'Modal Awal (1 Februari 2026)', amount: 'Rp 1.000.000.000', type: 'initial', icon: Calculator },
        { label: 'Setoran Modal / Investasi Tambahan', amount: 'Rp 0', type: 'positive', icon: Plus },
        { label: 'Laba Bersih Bulan Berjalan', amount: stats.balance, type: 'positive', icon: ArrowUpRight },
        { label: 'Penarikan Modal (Prive)', amount: '(Rp 0)', type: 'negative', icon: Minus },
        { label: 'Koreksi Laba/Rugi Tahun Lalu', amount: 'Rp 0', type: 'neutral', icon: Minus },
    ];

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-24 animate-in fade-in duration-700">
            <LaporanHeader
                title="Laporan Perubahan Modal"
                description="Analisis komposisi dan perubahan ekuitas unit sekolah selama periode berjalan"
                activeUnit={activeUnit}
                onUnitChange={setActiveUnit}
            />

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Statement Body */}
                <div className="lg:col-span-3 space-y-8">
                    <div className="bg-surface rounded-4xl border border-border-main shadow-xl overflow-hidden animate-in slide-in-from-left-4 duration-500">
                        <div className="p-8 border-b border-border-main bg-background/30 flex items-center gap-4">
                            <div className="w-12 h-12 bg-primary-soft text-primary rounded-2xl flex items-center justify-center">
                                <BarChart3 className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold uppercase tracking-tight">Statement of Changes in Equity</h3>
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest mt-0.5">Laporan Ekuitas Unit Sekolah</p>
                            </div>
                        </div>

                        <div className="p-0">
                            <table className="w-full text-left">
                                <tbody className="divide-y divide-border-main">
                                    {MODAL_CHANGES.map((item, i) => (
                                        <tr key={i} className="group hover:bg-primary-soft/5 transition-all">
                                            <td className="px-8 py-8">
                                                <div className="flex items-center gap-4">
                                                    <div className={`p-2 rounded-lg ${item.type === 'initial' ? 'bg-secondary-soft text-secondary' :
                                                        item.type === 'positive' ? 'bg-success-soft text-success' :
                                                            item.type === 'negative' ? 'bg-error-soft text-error' :
                                                                'bg-background text-muted'
                                                        }`}>
                                                        <item.icon className="w-4 h-4" />
                                                    </div>
                                                    <span className={`text-sm font-bold ${item.type === 'initial' ? 'text-foreground' : 'text-secondary group-hover:text-foreground transition-colors'}`}>
                                                        {item.label}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-8 text-right">
                                                <span className={`text-lg font-bold tabular-nums ${item.type === 'positive' ? 'text-success' :
                                                    item.type === 'negative' ? 'text-error' :
                                                        'text-foreground'
                                                    }`}>
                                                    {item.amount}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className="bg-primary/5">
                                        <td className="px-8 py-10 transition-all border-t-2 border-primary/20">
                                            <div className="flex items-center gap-4">
                                                <div className="p-2 bg-primary text-white rounded-lg shadow-lg shadow-primary/20">
                                                    <TrendingUp className="w-5 h-5" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-xl font-black text-foreground uppercase tracking-widest">Modal Akhir</span>
                                                    <span className="text-[10px] font-bold text-primary uppercase tracking-[0.2em]">Per 28 Februari 2026</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-10 text-right border-t-2 border-primary/20">
                                            <span className="text-3xl font-black text-primary tabular-nums tracking-tighter underline decoration-double decoration-primary/30 underline-offset-8">
                                                {stats.balance}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="p-8 bg-warning-soft/30 rounded-3xl border border-warning/10 flex items-start gap-4">
                        <div className="p-2 bg-warning/20 text-warning rounded-lg">
                            <Calculator className="w-4 h-4" />
                        </div>
                        <p className="text-xs text-secondary font-medium leading-relaxed">
                            <strong className="text-foreground">Catatan Audit:</strong> Laporan ini mencerminkan perubahan kekayaan bersih unit sekolah setelah memperhitungkan hasil usaha dan interaksi modal dengan yayasan selama satu bulan berjalan.
                        </p>
                    </div>
                </div>

                {/* Sidebar Metrics */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm space-y-6">
                        <h4 className="font-bold text-sm flex items-center gap-2">
                            <ArrowUpRight className="w-4 h-4 text-success" /> Pertumbuhan Modal
                        </h4>
                        <div className="space-y-4">
                            <div className="flex justify-between items-end">
                                <p className="text-[10px] font-black text-muted uppercase tracking-widest">Growth Rate</p>
                                <span className="text-2xl font-black text-foreground tabular-nums">{stats.balance}</span>
                            </div>
                            <div className="h-3 w-full bg-background rounded-full overflow-hidden">
                                <div className="h-full w-[14.6%] bg-success rounded-full opacity-70" />
                            </div>
                            <p className="text-[10px] text-secondary font-medium italic">
                                Peningkatan ekuitas dari kombinasi setoran modal dan akumulasi laba bersih.
                            </p>
                        </div>
                    </div>

                    <div className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm space-y-6">
                        <h4 className="font-bold text-sm flex items-center gap-2 text-warning">
                            <Calculator className="w-4 h-4" /> ROE (Return on Equity)
                        </h4>
                        <div className="text-center py-4 bg-background rounded-2xl border border-dashed border-border-main">
                            <h5 className="text-4xl font-black text-foreground">3.2%</h5>
                            <p className="text-[10px] font-bold text-muted uppercase tracking-widest mt-2">Bulan ini</p>
                        </div>
                        <p className="text-xs text-secondary font-medium leading-relaxed px-2">
                            Menunjukkan tingkat pengembalian laba terhadap total modal yang diinvestasikan pada unit ini.
                        </p>
                    </div>
                </div>
            </div>
        </main>
    );
}
